import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { renderPrice as currency } from 'core/utils';

// Import icons by individual modules for better code splitting
import CaretRight from 'core/icons/CaretRight';
import Gift from 'core/icons/Gift';

import { Content } from 'core/styles';

import Spinner from 'core/components/Spinner';
import { Divider } from 'listing/layout';
import LastDayToOrder, { notInPast } from 'listing/components/LastDayToOrder/LastDayContainer';
import ShippingDetails from 'listing/components/Shipping/ShippingDetails';
import { VariantsComponent } from 'listing/components/Variants';

import {
	OtherDetails,
	Price,
	SubHeading,
	CurrencyCode,
	Interval,
	Button,
	ShippingScheduleWrapper,
	DiscountWrapper,
	Discount,
	DealWrapper,
	Deal,
	Subprice,
	ClaimWrapper,
	SpinWrapper,
	BorderBox,
	Title,
	PolicyBox,
} from './pricingStyles';
import { ContactSeller } from 'core/components/ContactSeller';

const getShipsInDays = (days) => {
	if (days === null) return '';
	if (days === 1) return '24 hours';
	return `${days} days`;
};

const Pricing = ({
	lowestPrice,
	lowestVariantPrice,
	loadingPrice,
	retailValue,
	interval,
	otherDetails,
	shipping,
	buyBtnText,
	buyBtnProps,
	gifting,
	giftBtnText,
	giftBtnProps,
	promoCode,
	claimLink,
	productCurrentQuantity,
	badges,
	clean,
	variants,
	variantValues,
	onVariantChange,
	soldOut,
	dealDescription,
	currencyCode,
	currencySymbol,
	shippingSchedule,
	variantCurrentQuantity,
	isSticky,
	triggerLoginModal,
	isLoggedIn,
	email,
}) => {
	let isSoldOut = soldOut;
	let orderByBadges = badges.filter(badge => badge.badge === 'order_by');
	// Same check we do to render < LastDayToOrder />
	const validOrderByBadges = orderByBadges.filter(({ data }) => {
		const { orderBy, arriveBy, arriveByName } = data;
		return (notInPast(orderBy) && (arriveBy || arriveByName));
	});
	let soldOutText = 'Sold Out';
	// When this component is not used from ProductPageContainer, it will not have a lowest variant price.  Default to min_unit_term_price (lowestPrice)
	const price = typeof lowestVariantPrice !== 'undefined' ? lowestVariantPrice : lowestPrice;

	const isProductSoldOutOverall = productCurrentQuantity !== null && productCurrentQuantity !== undefined && productCurrentQuantity <= 0;
	const isVariantSelectionSoldOut = variantCurrentQuantity!== null && variantCurrentQuantity!== undefined && variantCurrentQuantity <= 0;
	if (isProductSoldOutOverall || isVariantSelectionSoldOut) {
		isSoldOut = true;
		if (isVariantSelectionSoldOut && !isProductSoldOutOverall) {
			soldOutText = 'Selection Sold Out';
		}
	}

	return (
		<div id="buy-btn">
			<BorderBox isSticky={isSticky}>
				{shippingSchedule && !orderByBadges.length && (
					<ShippingScheduleWrapper>
						Ships in <strong>{getShipsInDays(shippingSchedule)}</strong>
					</ShippingScheduleWrapper>
				)}
				{promoCode && (
					<DiscountWrapper>
						<Discount>{promoCode}</Discount>
					</DiscountWrapper>
				)}
				{dealDescription && (
					<DealWrapper>
						<Deal>{dealDescription}</Deal>
					</DealWrapper>
				)}
				{variants && (
					<Fragment>
						<VariantsComponent variants={variants} variantValues={variantValues} onVariantChange={onVariantChange}/>
						{variants.length > 0 && <Divider />}
					</Fragment>
				)}
				{!clean && <SubHeading>Plans as low as</SubHeading>}

				{loadingPrice ? (
					<SpinWrapper>
						<Spinner size="lg"/>
					</SpinWrapper>
				) : (
					<Price>
						{currency(price, currencySymbol)}
						{currencyCode !== 'USD' ? (<CurrencyCode> {currencyCode}</CurrencyCode>) : null}
						{interval && <Interval>{`/${interval}`}</Interval>}
					</Price>
				)}

				{retailValue &&
					<Subprice>{'('+ currency(retailValue) + '+ retail value)'}</Subprice>
				}

				{otherDetails && <OtherDetails>{otherDetails}</OtherDetails>}

				<Button size="lg" fullWidth {...buyBtnProps} disabled={isSoldOut}>
					{isSoldOut ? soldOutText : buyBtnText}
					<CaretRight stroke={"#fff"} fill={"#fff"} />
				</Button>

				{gifting && !isSoldOut && (
					<Button size="lg" themeType="stroke" fullWidth {...giftBtnProps} disabled={isSoldOut}>
						{giftBtnText ? giftBtnText : 'Give as a Gift'}
						<Gift fill={'currentColor'} />
					</Button>
				)}

				{claimLink && (
					<ClaimWrapper>
						{claimLink}
					</ClaimWrapper>
				)}

				{validOrderByBadges.length > 0 && <Divider />}
				{orderByBadges.length > 0 && orderByBadges.map(
					(badge, idx) => <LastDayToOrder key={`badge_${idx}`} {...badge.data} />
				)}
			</BorderBox>

			{shipping && (
				<BorderBox isSticky={isSticky}>
					{shipping && (
						<PolicyBox>
							<Title>Shipping Policy</Title>
							<ShippingDetails {...shipping} />
						</PolicyBox>
					)}

					<PolicyBox>
						<Title>Cancellation &amp; Refund Policy</Title>
						<Content as="div">
							<ul>
								<li>
									<ContactSeller
										triggerLogin={triggerLoginModal}
										isLoggedIn={isLoggedIn}
										email={email}
										element="link"
										label={`Contact Seller for questions about your box`}
										notLoggedLabel={`Contact Seller if you're unhappy with your box`}
									/>
								</li>
								<li>Subscriptions automatically renew. You can cancel at anytime.</li>
							</ul>
						</Content>
					</PolicyBox>
				</BorderBox>
			)}
		</div>
	);
};

export const pricingProps = {
	claimLink: PropTypes.element,
	lowestPrice: PropTypes.number.isRequired,
	lowestVariantPrice: PropTypes.number,
	loadingPrice: PropTypes.bool,
	retailValue: PropTypes.number,
	currencyCode: PropTypes.string,
	interval: PropTypes.string,
	otherDetails: PropTypes.string,
	shipping: PropTypes.object,
	shippingSchedule: PropTypes.number,
	promoCode: PropTypes.string,
	buyBtnText: PropTypes.string,
	buyBtnProps: PropTypes.object,
	gifting: PropTypes.bool,
	giftBtnText: PropTypes.string,
	giftBtnProps: PropTypes.object,
	badges: PropTypes.array,
	clean: PropTypes.bool,
	variants: PropTypes.array,
	variantValues: PropTypes.array,
	onVariantChange: PropTypes.func,
	soldOut: PropTypes.bool,
	dealDescription: PropTypes.string,
	currencySymbol: PropTypes.string,
	productCurrentQuantity: PropTypes.number,
	variantCurrentQuantity: PropTypes.number,
	isSticky: PropTypes.bool,
	triggerLoginModal: PropTypes.func,
	isLoggedIn: PropTypes.bool,
	email: PropTypes.string,
};

Pricing.propTypes = pricingProps;

Pricing.defaultProps = {
	claimLink: null,
	buyBtnText: 'Subscribe Now',
	buyBtnProps: {},
	giftBtnProps: {},
	badges: [],
	currencyCode: "USD",
	currencySymbol: '$',
};

export default Pricing;
